import { areaOf } from "./areas";
import {
  AIRLINE_CODE_PATTERN,
  ANY_AIRLINES,
  DEFAULT_ASSUMPTIONS,
  DEFAULT_ESTIMATE_TITLE,
  MAX_AIRLINE_PICKS,
  MAX_DESTINATIONS,
} from "./constants";
import { formatShortTripDates } from "./trip-dates";
import type {
  AirlinePreference,
  AssumptionField,
  Assumptions,
  ChatTouchKey,
  EditSource,
  Origin,
  Workspace,
  WorkspaceAction,
} from "./types";

/**
 * The airline choice a search actually runs with: codes uppercased, deduplicated and sorted, and
 * "only" or "not these" with no airlines picked read as any, so picking a mode alone searches nothing new.
 * @param airlines - The planner's choice.
 * @returns The choice, normalized; names kept only for the codes that remain.
 * @throws Never.
 */
export const effectiveAirlines = (airlines: AirlinePreference): AirlinePreference => {
  const codes = [...new Set(airlines.codes.map((code) => code.trim().toUpperCase()))].sort();
  if (airlines.mode === "any" || codes.length === 0) return ANY_AIRLINES;
  const names = Object.fromEntries(
    codes.flatMap((code) => {
      const name = airlines.names[code];
      return name ? [[code, name]] : [];
    })
  );
  return { mode: airlines.mode, codes, names };
};

/**
 * Normalizes a city name for comparison, so "porto" and " Porto " are one city.
 * @param city - Raw city name.
 * @returns Lowercased, trimmed, single-spaced name.
 * @throws Never.
 */
export const cityKey = (city: string): string => city.trim().replace(/\s+/g, " ").toLowerCase();

/**
 * Tidies a city name for display. A name typed all in lowercase gets each word capitalized
 * ("san diego" becomes "San Diego"); any other casing is kept, so "NYC" and "McAllen" stay.
 * @param city - Raw city name.
 * @returns Trimmed, single-spaced name.
 * @throws Never.
 */
export const displayCity = (city: string): string => {
  const tidy = city.trim().replace(/\s+/g, " ");
  if (tidy === tidy.toLowerCase())
    return tidy.replace(/(^|[\s-])(\p{L})/gu, (_, gap: string, letter: string) => gap + letter.toUpperCase());
  return tidy.charAt(0).toUpperCase() + tidy.slice(1);
};

/**
 * A blank workspace in the given currency.
 * @param currency - Display currency, usually the user's preference.
 * @returns A workspace with no cities, no dates and the default assumptions.
 * @throws Never.
 */
export const createWorkspace = (currency: string): Workspace => ({
  title: DEFAULT_ESTIMATE_TITLE,
  origins: [],
  headcount: 0,
  destinations: [],
  dates: null,
  cabin: "economy",
  currency,
  assumptions: DEFAULT_ASSUMPTIONS,
  attendeeFileName: null,
  chatTouched: [],
  fareOverrides: {},
  airports: {},
  airlines: ANY_AIRLINES,
});

/**
 * Identity of a traveler group: its team (if any) and home city. Two teams in one city stay apart.
 * @param origin - The group.
 * @returns A stable key.
 * @throws Never.
 */
export const originKey = (origin: Pick<Origin, "city" | "group">): string =>
  origin.group ? `${cityKey(origin.group)}@${cityKey(origin.city)}` : cityKey(origin.city);

/**
 * The name a group goes by in the flight pipeline: its team, else its home city.
 * @param origin - The group.
 * @returns The group name.
 * @throws Never.
 */
export const groupName = (origin: Pick<Origin, "city" | "group">): string => origin.group?.trim() || origin.city;

/**
 * Key for one traveler group to destination route, independent of dates and cabin.
 * @param key - The group's {@link originKey}.
 * @param destination - Destination city.
 * @returns A stable key.
 * @throws Never.
 */
export const routeKey = (key: string, destination: string): string => `${key}|${cityKey(destination)}`;

/**
 * What to price a city from: the airport the planner picked, else the airport an area flies
 * into ("Napa" flies into SFO), else the city name.
 * @param city - City name.
 * @param workspace - Supplies airport picks.
 * @returns An IATA code or the city name.
 * @throws Never.
 */
export const placeFor = (city: string, workspace: Pick<Workspace, "airports">): string =>
  workspace.airports[cityKey(city)] ?? areaOf(city)?.airports[0] ?? city;

/**
 * The touch keys an action changes, for the "added by chat" highlight.
 * @param action - The edit.
 * @returns Keys of the fields it changes.
 * @throws Never.
 */
const touchedBy = (action: WorkspaceAction): ChatTouchKey[] => {
  switch (action.type) {
    case "addDestination":
      return [`destination:${cityKey(action.city)}`];
    case "setOrigins":
      return action.origins.map((origin) => `origin:${originKey(origin)}` as const);
    case "setOriginCount":
      return [`origin:${action.key}`];
    case "setDates":
      return ["dates"];
    case "setAssumptions":
      return ["assumptions"];
    case "setCabin":
      return ["cabin"];
    case "setAirlines":
      return ["airlines"];
    default:
      return [];
  }
};

/** Prefix shared by every key one action family owns, so a user edit clears the whole family. */
const touchFamily = (key: ChatTouchKey): string => key.split(":")[0];

/**
 * Why an action cannot apply, or null when it can.
 * @param workspace - The current workspace.
 * @param action - The proposed edit.
 * @returns A short user-facing reason, or null.
 * @throws Never.
 */
export const rejectReason = (workspace: Workspace, action: WorkspaceAction): string | null => {
  if (action.type === "addDestination") {
    if (!action.city.trim()) return "City name is empty";
    if (workspace.destinations.some((city) => cityKey(city) === cityKey(action.city))) {
      return `${displayCity(action.city)} is already in the comparison`;
    }
    if (workspace.destinations.length >= MAX_DESTINATIONS) {
      return `The comparison holds ${MAX_DESTINATIONS} cities. Remove one first.`;
    }
  }
  if (action.type === "removeDestination") {
    if (!workspace.destinations.some((city) => cityKey(city) === cityKey(action.city))) {
      return `${displayCity(action.city)} is not in the comparison`;
    }
  }
  if (
    (action.type === "setOriginCount" || action.type === "setOriginCabin") &&
    !workspace.origins.some((origin) => originKey(origin) === action.key)
  ) {
    return "That group is no longer in the estimate";
  }
  if (action.type === "setOriginCount" && !(Number.isInteger(action.count) && action.count >= 1)) {
    return "Enter at least 1 person";
  }
  if (action.type === "setAirport" && action.code !== null && !/^[A-Za-z]{3}$/.test(action.code.trim())) {
    return "Use a 3-letter airport code";
  }
  if (action.type === "setFareOverride" && action.usd !== null && !(action.usd >= 0)) {
    return "Enter a fare of 0 or more";
  }
  if (action.type === "setAirlines") {
    if (action.airlines.codes.some((code) => !AIRLINE_CODE_PATTERN.test(code.trim().toUpperCase()))) {
      return "Use 2-character airline codes";
    }
    if (action.airlines.codes.length > MAX_AIRLINE_PICKS) return `Pick up to ${MAX_AIRLINE_PICKS} airlines`;
  }
  if (action.type === "setDates" && action.dates && action.dates.end < action.dates.start) {
    return "The trip ends before it starts";
  }
  return null;
};

/**
 * Applies one edit. Invalid edits (see {@link rejectReason}) return the workspace unchanged.
 * @param workspace - The current workspace.
 * @param action - The edit.
 * @param source - Who made it. Chat edits are highlighted; user edits clear the highlight they touch.
 * @returns The next workspace.
 * @throws Never.
 */
export const applyAction = (workspace: Workspace, action: WorkspaceAction, source: EditSource): Workspace => {
  if (rejectReason(workspace, action)) return workspace;
  const reduced = reduce(workspace, action);
  // Until the planner names it, the title follows the trip.
  const next =
    action.type !== "setTitle" && action.type !== "replace" && hasAutoTitle(workspace)
      ? { ...reduced, title: autoTitle(reduced) }
      : reduced;
  const touched = touchedBy(action);
  if (source === "chat") {
    return { ...next, chatTouched: [...new Set([...next.chatTouched, ...touched])] };
  }
  const families = new Set(touched.map(touchFamily));
  return { ...next, chatTouched: next.chatTouched.filter((key) => !families.has(touchFamily(key))) };
};

const ASSUMPTION_FIELDS: readonly AssumptionField[] = ["meetingSpacePerDay", "mealsPerDay"];

/**
 * Applies a rate change and marks each rate it sets as the planner's, so that rate stays flat
 * across cities. A change that carries its own `edited` list (an undo) restores that list as is.
 */
const applyAssumptions = (current: Assumptions, change: Partial<Assumptions>): Assumptions => {
  const set = ASSUMPTION_FIELDS.filter((field) => change[field] !== undefined);
  const edited = change.edited ?? [...new Set([...(current.edited ?? []), ...set])];
  return { ...current, ...change, edited };
};

const reduce = (workspace: Workspace, action: WorkspaceAction): Workspace => {
  switch (action.type) {
    case "setOrigins":
      return {
        ...workspace,
        origins: mergeOrigins(action.origins),
        attendeeFileName: action.fileName === undefined ? workspace.attendeeFileName : action.fileName,
      };
    case "setOriginCount":
      return {
        ...workspace,
        origins: workspace.origins.map((origin) =>
          originKey(origin) === action.key ? { ...origin, count: action.count } : origin
        ),
      };
    case "setOriginCabin":
      return {
        ...workspace,
        origins: workspace.origins.map((origin) => {
          if (originKey(origin) !== action.key) return origin;
          const { cabin: _cabin, ...rest } = origin;
          return action.cabin ? { ...rest, cabin: action.cabin } : rest;
        }),
      };
    case "setFareOverride": {
      const key = routeKey(action.originKey, action.destination);
      const { [key]: _removed, ...rest } = workspace.fareOverrides;
      return { ...workspace, fareOverrides: action.usd === null ? rest : { ...rest, [key]: action.usd } };
    }
    case "setAirport": {
      const key = cityKey(action.city);
      const { [key]: _removed, ...rest } = workspace.airports;
      return {
        ...workspace,
        airports: action.code === null ? rest : { ...rest, [key]: action.code.trim().toUpperCase() },
      };
    }
    case "addDestination": {
      const destinations = [...workspace.destinations];
      destinations.splice(action.at ?? destinations.length, 0, displayCity(action.city));
      // A city added without a region (typed, or by the chat) forgets any earlier pick's region.
      const { [cityKey(action.city)]: _stale, ...places } = workspace.places ?? {};
      return {
        ...workspace,
        destinations,
        places: action.region ? { ...places, [cityKey(action.city)]: action.region } : places,
      };
    }
    case "removeDestination":
      return {
        ...workspace,
        destinations: workspace.destinations.filter((city) => cityKey(city) !== cityKey(action.city)),
        chatTouched: workspace.chatTouched.filter((key) => key !== `destination:${cityKey(action.city)}`),
      };
    case "setDates":
      return { ...workspace, dates: action.dates };
    case "setAssumptions":
      return { ...workspace, assumptions: applyAssumptions(workspace.assumptions, action.assumptions) };
    case "setCabin":
      return { ...workspace, cabin: action.cabin };
    case "setAirlines": {
      // The mode is kept as picked, even with no airlines yet, so the picker stays open on it.
      const codes = [...new Set(action.airlines.codes.map((code) => code.trim().toUpperCase()))];
      return { ...workspace, airlines: { mode: action.airlines.mode, codes, names: action.airlines.names } };
    }
    case "setCurrency":
      return { ...workspace, currency: action.currency };
    case "setHeadcount":
      return { ...workspace, headcount: Math.max(0, Math.round(action.headcount)) };
    case "setTitle":
      return { ...workspace, title: action.title };
    case "replace":
      return action.workspace;
  }
};

/** Collapses duplicate cities into one row and drops empty ones. */
const mergeOrigins = (origins: Workspace["origins"]): Workspace["origins"] => {
  const merged = new Map<string, Origin>();
  for (const origin of origins) {
    const count = Math.round(origin.count);
    if (!origin.city.trim() || count <= 0) continue;
    const key = originKey(origin);
    const existing = merged.get(key);
    merged.set(key, {
      ...origin,
      city: existing?.city ?? displayCity(origin.city),
      group: origin.group?.trim() || undefined,
      count: (existing?.count ?? 0) + count,
    });
  }
  return [...merged.values()];
};

/**
 * The edit that reverses `action` when applied to the workspace that results from it.
 * @param before - The workspace before `action`.
 * @param action - The edit being made.
 * @returns The inverse edit.
 * @throws Never.
 */
export const inverseOf = (before: Workspace, action: WorkspaceAction): WorkspaceAction => {
  switch (action.type) {
    case "addDestination":
      return { type: "removeDestination", city: action.city };
    case "removeDestination": {
      const at = before.destinations.findIndex((city) => cityKey(city) === cityKey(action.city));
      const region = before.places?.[cityKey(action.city)];
      return { type: "addDestination", city: action.city, ...(at >= 0 ? { at } : {}), ...(region ? { region } : {}) };
    }
    case "setOrigins":
      return { type: "setOrigins", origins: before.origins, fileName: before.attendeeFileName };
    case "setOriginCount":
      return {
        type: "setOriginCount",
        key: action.key,
        count: before.origins.find((origin) => originKey(origin) === action.key)?.count ?? action.count,
      };
    case "setOriginCabin":
      return {
        type: "setOriginCabin",
        key: action.key,
        cabin: before.origins.find((origin) => originKey(origin) === action.key)?.cabin ?? null,
      };
    case "setFareOverride":
      return {
        type: "setFareOverride",
        originKey: action.originKey,
        destination: action.destination,
        usd: before.fareOverrides[routeKey(action.originKey, action.destination)] ?? null,
      };
    case "setAirport":
      return { type: "setAirport", city: action.city, code: before.airports[cityKey(action.city)] ?? null };
    case "setDates":
      return { type: "setDates", dates: before.dates };
    case "setAssumptions":
      // Carries the edited list too, so an undone edit goes back to adjusting per city.
      return {
        type: "setAssumptions",
        assumptions: { ...before.assumptions, edited: before.assumptions.edited ?? [] },
      };
    case "setCabin":
      return { type: "setCabin", cabin: before.cabin };
    case "setAirlines":
      return { type: "setAirlines", airlines: before.airlines };
    case "setCurrency":
      return { type: "setCurrency", currency: before.currency };
    case "setHeadcount":
      return { type: "setHeadcount", headcount: before.headcount };
    case "setTitle":
      return { type: "setTitle", title: before.title };
    case "replace":
      return { type: "replace", workspace: before };
  }
};

/**
 * People whose home city is known: the sum of the home-city counts.
 * @param workspace - The workspace.
 * @returns Number of travelers with a home city.
 * @throws Never.
 */
export const knownTravelers = (workspace: Workspace): number =>
  workspace.origins.reduce((sum, origin) => sum + origin.count, 0);

/**
 * People counted in the headcount (e.g. an event's) but not yet placed in a home city. The
 * headcount is a floor on the total, so adding "Austin 7" to 30 leaves 23 without a home city.
 * @param workspace - The workspace.
 * @returns Number of travelers without a home city; their flights aren't priced.
 * @throws Never.
 */
export const unknownTravelers = (workspace: Workspace): number =>
  Math.max(0, workspace.headcount - knownTravelers(workspace));

/**
 * The people the estimate covers: everyone with a home city plus anyone the headcount says is
 * coming without one.
 * @param workspace - The workspace.
 * @returns Number of travelers.
 * @throws Never.
 */
export const travelerCount = (workspace: Workspace): number => knownTravelers(workspace) + unknownTravelers(workspace);

const MONTH = new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" });

const monthOf = (isoDate: string): string => MONTH.format(new Date(`${isoDate.slice(0, 10)}T00:00:00Z`));

const describeCities = (cities: string[], many: string): string | null => {
  if (cities.length === 0) return null;
  if (cities.length === 1) return displayCity(cities[0]);
  if (cities.length === 2) return `${displayCity(cities[0])} or ${displayCity(cities[1])}`;
  return `${cities.length} ${many}`;
};

/**
 * The distinct home cities, the city with the most travelers first. A city split into groups (by
 * team or cabin) is one home city.
 * @param origins - The traveler groups.
 * @returns The city names, one per home city.
 * @throws Never.
 */
export const homeCities = (origins: Origin[]): string[] => {
  const travelers = new Map<string, { city: string; count: number }>();
  for (const origin of origins) {
    const entry = travelers.get(cityKey(origin.city));
    if (entry) entry.count += origin.count;
    else travelers.set(cityKey(origin.city), { city: origin.city, count: origin.count });
  }
  return [...travelers.values()].sort((a, b) => b.count - a.count).map((entry) => entry.city);
};

/**
 * The name an estimate gets from its trip, e.g. "London → Lisbon · 12 people · Nov".
 * @param workspace - The workspace.
 * @returns The generated title, or the default title when there is nothing to name it by.
 * @throws Never.
 */
export const autoTitle = (workspace: Workspace): string => {
  const from = describeCities(homeCities(workspace.origins), "home cities")?.replace(" or ", " and ");
  const to = describeCities(workspace.destinations, "cities");
  const route = from && to ? `${from} → ${to}` : (to ?? (from ? `From ${from}` : null));
  const people = travelerCount(workspace);
  const dates = workspace.dates;
  const month = dates
    ? monthOf(dates.start) === monthOf(dates.end)
      ? monthOf(dates.start)
      : `${monthOf(dates.start)}–${monthOf(dates.end)}`
    : null;
  // Dates alone read as the dates ("Oct 22–24"), not a bare month. No year: saved titles were
  // generated without one, and `hasAutoTitle` must still recognize them.
  const when = dates && !route && people === 0 ? formatShortTripDates(dates, false) : month;
  const parts = [
    route,
    people > 0 ? `${people.toLocaleString("en-US")} ${people === 1 ? "person" : "people"}` : null,
    when,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : DEFAULT_ESTIMATE_TITLE;
};

/**
 * Whether the workspace still carries a generated title, so edits keep renaming it.
 * @param workspace - The workspace.
 * Compared ignoring case, so a title generated before city names were capitalized still counts.
 * @returns True until the planner (or the chat) names the estimate.
 * @throws Never.
 */
export const hasAutoTitle = (workspace: Workspace): boolean =>
  workspace.title === DEFAULT_ESTIMATE_TITLE || workspace.title.toLowerCase() === autoTitle(workspace).toLowerCase();
